import { experimental_extendTheme as extendTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import cssVarJson from './export.json';
import MierB from './fonts/MierB/MierB-Regular.woff2';
import { fixThemeOptions } from '../../utils/theme';
const CSS_VAR_PREFIX = 'alternaleaf'; // Needs to be unique to support theme nesting
const baseFontFamily = 'Mier B, Helvetica, Arial, sans-serif';
const customizations = {
    cssVarPrefix: CSS_VAR_PREFIX,
    spacing: (factor) => `${0.25 * factor}rem`,
    typography: {
        fontFamily: baseFontFamily
    },
    components: {
        // MuiAlert overrides to fix the broken logic in extend theme.
        MuiAlert: {
            styleOverrides: {
                standardError: {
                    backgroundColor: `var(--${CSS_VAR_PREFIX}-palette-error-light)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-error-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-error-dark)` // Different color for message text
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit'
                        }
                    }
                },
                standardWarning: {
                    backgroundColor: '#FFF4E5',
                    color: `var(--${CSS_VAR_PREFIX}-palette-warning-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-warning-dark)`
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit'
                        }
                    }
                },
                standardInfo: {
                    backgroundColor: '#E5F6FD',
                    color: `var(--${CSS_VAR_PREFIX}-palette-info-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-info-dark)`
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit'
                        }
                    }
                },
                standardSuccess: {
                    backgroundColor: '#EDF7ED',
                    color: `var(--${CSS_VAR_PREFIX}-palette-success-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-success-dark)`
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit'
                        }
                    }
                },
                outlinedError: {
                    borderColor: `var(--${CSS_VAR_PREFIX}-palette-error-main)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-error-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-error-dark)`
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit'
                        }
                    }
                },
                outlinedWarning: {
                    borderColor: `var(--${CSS_VAR_PREFIX}-palette-warning-main)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-warning-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-warning-dark)`
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit'
                        }
                    }
                },
                outlinedInfo: {
                    borderColor: `var(--${CSS_VAR_PREFIX}-palette-info-main)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-info-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-info-dark)`
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit'
                        }
                    }
                },
                outlinedSuccess: {
                    borderColor: `var(--${CSS_VAR_PREFIX}-palette-success-main)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-success-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-success-dark)`
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit'
                        }
                    }
                }
            }
        },
        MuiCssBaseline: {
            styleOverrides: `
        @font-face {
          font-family: 'Mier B';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: local('Mier B'), local('Mier B-Regular'), url(${MierB}) format('woff2');
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }
      `
        },
        // Temporary fixes to the export. Once the figma is fixed and exported properly, these should be removed
        MuiButton: {
            styleOverrides: {
                sizeLarge: ({ ownerState }) => ({
                    padding: ownerState.variant === 'text' ? '8px 11px' : '8px 22px' // Export bug causing button hover padding change
                }),
                sizeMedium: ({ ownerState }) => ({
                    padding: ownerState.variant === 'text' ? '6px 8px' : '6px 16px'
                }),
                sizeSmall: ({ ownerState }) => ({
                    padding: ownerState.variant === 'text' ? '4px 5px' : '4px 10px'
                })
            }
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontSize: '0.875rem',
                    fontWeight: 600
                }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: cssVarJson.shape.paperRadius ?? 8 // todo: Confirm where UX got "paperRadius" from
                }
            }
        }
    }
};
export const alternaleafJSON = fixThemeOptions(deepmerge(cssVarJson, customizations));
export const alternaleaf = extendTheme(alternaleafJSON);
export default alternaleaf;
//# sourceMappingURL=data:application/json;base64,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